import React, { useEffect, useState } from 'react'
import { Button, Col, List, Modal, Row, Select, Spin, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight'
import { faSearch } from '@fortawesome/pro-solid-svg-icons/faSearch'
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes'
import Loader from 'modules/rankmi-table/components/Loader'
import { debounce } from 'helpers/debounce'
import { powerfulTextCompare } from '../../helpers/filter'
import AreasBreadcrumb from './AreasBreadcrumb.component'
import { IAreaInitialState } from '../../interfaces/areas'
import type { IArea } from '../../interfaces/areas'
import styles from './AreasModal.module.scss'

interface IAreasModalProps {
  show: boolean
  areas: IArea[]
  className?: string
  selected?: IArea
  onAreaSelected: any
  onClose: any
  translations: any
  loading?: boolean
}

const AreasModal: React.FC<IAreasModalProps> = ({
  show = false,
  areas = [],
  className = '',
  onAreaSelected,
  onClose,
  translations,
  selected = undefined,
  loading = false,
}) => {
  const TRUNCATE_NUMBER = 50
  const [dataSource, setDataSource] = useState([] as IArea[])
  const [searchDataSource, setSearchDataSource] = useState([] as IArea[])

  const [parentAreas, setParentAreas] = useState([] as IArea[])
  const [childAreas, setChildAreas] = useState([] as IArea[])
  const [selectedParentArea, setSelectedParentArea] =
    useState(IAreaInitialState)
  const [selectedArea, setSelectedArea] = useState(IAreaInitialState)

  useEffect(() => {
    setParentAreas(areas.filter(({ parentId }) => !parentId))
    setDataSource(areas)
    setSearchDataSource(areas.slice(0, TRUNCATE_NUMBER))
  }, [areas])

  useEffect(() => {
    if (selected?.id && selected.id !== selectedArea.id) {
      handleSearch(selected.id)
    } else if (dataSource.length > 0) {
      setSelectedArea(dataSource[0])
      setSelectedParentArea(dataSource[0])
    }
  }, [dataSource])

  useEffect(() => {
    getChildAreas(selectedParentArea.id)
  }, [selectedParentArea])

  const getChildAreas = (parent: number) => {
    setChildAreas(dataSource.filter(({ parentId }) => parentId === parent))
  }

  const selectChildArea = (area: IArea) => {
    if (dataSource.find(({ parentId }) => parentId === area.id)) {
      getChildAreas(area.id)
      setParentAreas([area])
      setSelectedParentArea(area)
    }
    setSelectedArea(area)
  }

  const handleSelectParentArea = (area: IArea) => {
    setSelectedParentArea(area)
    setSelectedArea(area)
  }

  const hasChild = (id: number) =>
    !!dataSource.find(({ parentId }) => parentId === id)

  const getParentName = (parentId: number) => {
    const { name } = dataSource.find(({ id }) => id === parentId) || {
      name: '',
    }
    return name
  }

  const handleSearch = (selectedAreaId: number) => {
    const auxSelectedArea =
      dataSource.find(({ id }) => id === selectedAreaId) || IAreaInitialState
    if (!auxSelectedArea.parentId) {
      setParentAreas(dataSource.filter(({ parentId }) => !parentId))
      setSelectedParentArea(auxSelectedArea)
    } else if (hasChild(auxSelectedArea.id)) {
      setParentAreas([auxSelectedArea])
      setSelectedParentArea(auxSelectedArea)
    } else {
      const auxSelectedParentArea =
        dataSource.find(({ id }) => id === auxSelectedArea.parentId) ||
        IAreaInitialState
      if (!auxSelectedParentArea.parentId) {
        setParentAreas(dataSource.filter(({ parentId }) => !parentId))
      } else {
        setParentAreas([auxSelectedParentArea])
      }
      setSelectedParentArea(auxSelectedParentArea)
    }
    setSelectedArea(auxSelectedArea)
  }

  const debouncedSetSearchDataSource = debounce(
    (searchTerm: string) => handleInputSearch(searchTerm),
    500,
  )

  const handleInputSearch = (value: string) => {
    if (!value || value === '') {
      const truncatedAreas = areas.slice(0, TRUNCATE_NUMBER)
      setSearchDataSource(truncatedAreas)
      return
    }

    if (value.length >= 3) {
      const filteredDataSource = dataSource
        .filter(({ name }) => powerfulTextCompare(name, value))
        .slice(0, TRUNCATE_NUMBER)
      setSearchDataSource(filteredDataSource)
    }
  }
  const handleSubmit = () => {
    onAreaSelected(selectedArea)
    onClose()
  }

  const truncate = (text: string) => {
    const aux = text.substring(0, 33).lastIndexOf(' ')
    return aux > 0
      ? `${text.substring(0, aux)}...`
      : `${text.substring(0, 29)}...`
  }

  return (
    <Modal
      cancelText={translations.buttons.cancel}
      className={`rankmiTable-areas-modal ${className} ${styles.container}`}
      closeIcon={<FontAwesomeIcon icon={faTimes} />}
      okText={translations.buttons.accept}
      title={translations.header}
      visible={show}
      width={600}
      onCancel={() => onClose()}
      onOk={() => {
        handleSubmit()
      }}
    >
      <Row className={`w-100 ${styles.searchRow}`}>
        <Col>
          <Select
            showSearch
            className="w-100 rankmiTable-areas-modal-search"
            dropdownClassName="rankmiTable-areas-modal-search-dropdown"
            onSearch={(value: string) => debouncedSetSearchDataSource(value)}
            optionLabelProp="label"
            placeholder={translations.search}
            suffixIcon={<FontAwesomeIcon icon={faSearch} />}
            onChange={handleSearch}
            filterOption={false}
          >
            {searchDataSource.map(({ id, name, parentId }, index) => (
              <Select.Option
                className="rankmiTable-areas-modal-search-dropdown-option"
                key={`search-item-${index}-${id}`}
                label={name}
                value={id}
              >
                <div>
                  <div className="w-100 option-name">{name}</div>
                  <div className="w-100 option-parent">
                    {parentId && (
                      <span>{`${translations.dependsOn} ${getParentName(parentId)}`}</span>
                    )}
                  </div>
                </div>
              </Select.Option>
            ))}
          </Select>
        </Col>
      </Row>

      <Row className={styles.breadcrumbRow}>
        <AreasBreadcrumb
          dataSource={dataSource}
          selectedArea={selectedArea}
          onClick={handleSearch}
        />
      </Row>

      <Row className={`${styles.areasRow}`}>
        <Spin className="h-100" indicator={<Loader />} spinning={loading}>
          <Col
            className={`${styles.parentAreaContainer} rankmiTable-areas-modal-parentAreas`}
            xs={12}
          >
            {parentAreas.map((area, index) => (
              <div key={`parentArea-${index}`}>
                {area.name.length >= 33 ? (
                  <Tooltip placement="right" title={area.name}>
                    <Button
                      className={
                        selectedArea.id === area.id ? styles.active : ''
                      }
                      size="small"
                      onClick={() => {
                        handleSelectParentArea(area)
                      }}
                    >
                      {truncate(area.name)}{' '}
                      {hasChild(area.id) && (
                        <FontAwesomeIcon icon={faChevronRight} />
                      )}
                    </Button>
                  </Tooltip>
                ) : (
                  <Button
                    className={selectedArea.id === area.id ? styles.active : ''}
                    size="small"
                    onClick={() => {
                      handleSelectParentArea(area)
                    }}
                  >
                    {area.name}{' '}
                    {hasChild(area.id) && (
                      <FontAwesomeIcon icon={faChevronRight} />
                    )}
                  </Button>
                )}
                {/* {area.name} {hasChild(area.id) && <FontAwesomeIcon icon={faChevronRight} />} */}
              </div>
            ))}
          </Col>
          <Col
            className={`${styles.childAreasContainer} rankmiTable-areas-modal-childAreas`}
            xs={12}
          >
            <List
              dataSource={childAreas}
              itemLayout="horizontal"
              locale={{ emptyText: <div /> }}
              renderItem={(area: IArea, index) => (
                <List.Item key={`childArea-${index}`}>
                  {area.name.length > 33 ? (
                    <Tooltip placement="right" title={area.name}>
                      <Button
                        className={
                          selectedArea.id === area.id ? styles.active : ''
                        }
                        size="small"
                        onClick={() => {
                          selectChildArea(area)
                        }}
                      >
                        {truncate(area.name)}{' '}
                        {hasChild(area.id) && (
                          <FontAwesomeIcon icon={faChevronRight} />
                        )}
                      </Button>
                    </Tooltip>
                  ) : (
                    <Button
                      className={
                        selectedArea.id === area.id ? styles.active : ''
                      }
                      size="small"
                      onClick={() => {
                        selectChildArea(area)
                      }}
                    >
                      {area.name}{' '}
                      {hasChild(area.id) && (
                        <FontAwesomeIcon icon={faChevronRight} />
                      )}
                    </Button>
                  )}
                </List.Item>
              )}
            />
          </Col>
        </Spin>
      </Row>
    </Modal>
  )
}

export default AreasModal
